import { useEffect, useState } from "react";
import {
  Box,
  Heading,
  useToast,
  Button,
  FormControl,
  FormLabel,
  Input,
  useColorModeValue,
  Center,
  VStack,
  Text,
  Stack,
  SimpleGrid,
  Container,
  Radio,
  RadioGroup,
  HStack,
} from "@chakra-ui/react";
import { Scanner as QrScanner } from "@yudiel/react-qr-scanner";
import { HiQrcode } from "react-icons/hi";

import Navbar from "../../components/Navbar";
import useAxios from "../../utils/useAxios";

const QrCodeLabelScanner = ({ setLabelCode, setQRscanVisible }: any) => {
  const [data, setData] = useState("");

  useEffect(() => {
    if (data) {
      setLabelCode(data);
      setQRscanVisible(false);
    }
  }, [data, setLabelCode, setQRscanVisible]);

  return (
    <Box
      bg="gray.100"
      borderRadius="3px"
      h="350px"
      w="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      overflow="hidden"
      mt={4}
    >
      <QrScanner
        onScan={(codes: any[]) => {
          if (!codes || !codes.length) {
            return;
          }

          const code = codes[0]?.rawValue || "";
          const regex = /^[a-zA-Z0-9]*$/;
          if (!regex.test(code)) {
            console.log("Invalid QR code");
            return;
          }

          setData(code);
        }}
        onError={() => {
          console.log("Error occurred while scanning.");
        }}
        constraints={{ facingMode: "environment" }}
        scanDelay={200}
        sound={true}
        styles={{
          container: { width: "100%", height: "100%" },
          video: { width: "100%", height: "100%", objectFit: "cover" },
        }}
      />
    </Box>
  );
};

interface ITrainingData {
  device_address?: string;
  label_code?: string;
  real_moisture?: number;
  soil_category?: number;
  nutrient_level?: number;
  sensor_version?: number;
  measurement_time_minutes?: number;
}

const AddTrainingDataView = () => {
  const toast = useToast();
  const api = useAxios();

  const [identifierType, setIdentifierType] = useState<"device_address" | "label_code">(
    "device_address",
  );
  const [deviceAddress, setDeviceAddress] = useState("");
  const [labelCode, setLabelCode] = useState("");
  const [QRscanVisible, setQRscanVisible] = useState(false);
  const [moisture, setMoisture] = useState<number | undefined>(undefined);
  const [soilCategory, setSoilCategory] = useState<number | undefined>(undefined);
  const [nutrientLevel, setNutrientLevel] = useState<number | undefined>(undefined);
  const [deviceVersion, setDeviceVersion] = useState<number | undefined>(undefined);
  const [measurementTime, setMeasurementTime] = useState<number | undefined>(15);

  const handleSubmit = async () => {
    const currentIdentifier = identifierType === "device_address" ? deviceAddress : labelCode;
    if (currentIdentifier && moisture !== null && deviceVersion !== null) {
      const trainingData: ITrainingData = {
        real_moisture: moisture,
        soil_category: soilCategory,
        nutrient_level: nutrientLevel,
        sensor_version: deviceVersion,
        measurement_time_minutes: measurementTime,
      };

      if (identifierType === "device_address") {
        trainingData.device_address = deviceAddress;
      } else {
        trainingData.label_code = labelCode;
      }

      try {
        const response = await api.post("ml/create-soil-training-sample/", trainingData);
        setMoisture(undefined);
        setNutrientLevel(undefined);
        toast({
          title: "Data submitted successfully. " + response.data.measurements + " samples logged.",
          status: "success",
        });
        console.log(response.data);
      } catch (err: any) {
        console.log(err);
        toast({ title: err.message || "An error occurred", status: "error" });
      }
    }
  };

  return (
    <Navbar>
      <Container p="4" mt="4" borderRadius={8} bg={useColorModeValue("white", "gray.800")}>
        <Heading>Add Training Data</Heading>
        <Text my="2"> Log a training sample based on the last x minutes </Text>

        <FormControl id="identifierType" isRequired>
          <FormLabel>Device Identification Method</FormLabel>
          <RadioGroup
            value={identifierType}
            onChange={(value) => setIdentifierType(value as "device_address" | "label_code")}
          >
            <Stack direction="row">
              <Radio value="device_address">Device Address</Radio>
              <Radio value="label_code">Label Code</Radio>
            </Stack>
          </RadioGroup>
        </FormControl>

        {identifierType === "device_address" ? (
          <FormControl id="deviceAddress" isRequired>
            <FormLabel>Device Address</FormLabel>
            <Input
              type="text"
              value={deviceAddress}
              onChange={(e) => setDeviceAddress(e.target.value)}
            />
          </FormControl>
        ) : (
          <>
            <FormControl id="labelCode" isRequired>
              <FormLabel>Label Code</FormLabel>
              <HStack>
                <Input
                  type="text"
                  value={labelCode}
                  onChange={(e) => setLabelCode(e.target.value)}
                />
                <Button
                  colorScheme="teal"
                  variant="outline"
                  onClick={() => setQRscanVisible(!QRscanVisible)}
                  leftIcon={<HiQrcode />}
                >
                  Scan QR
                </Button>
              </HStack>
            </FormControl>
            {QRscanVisible && (
              <QrCodeLabelScanner setLabelCode={setLabelCode} setQRscanVisible={setQRscanVisible} />
            )}
          </>
        )}

        <FormControl id="deviceVersion" isRequired>
          <FormLabel>Device Version</FormLabel>
          <Input
            type="number"
            value={deviceVersion}
            onChange={(e) => setDeviceVersion(e.target.value ? Number(e.target.value) : undefined)}
          />
        </FormControl>
        <FormControl id="soilCategory">
          <FormLabel>Soil Category</FormLabel>
          <Input
            type="number"
            value={soilCategory}
            onChange={(e) => setSoilCategory(e.target.value ? Number(e.target.value) : undefined)}
          />
        </FormControl>
        <FormControl id="moisture" isRequired>
          <FormLabel>Moisture</FormLabel>
          <Input
            type="number"
            value={moisture === undefined ? "" : moisture}
            onChange={(e) => setMoisture(e.target.value ? Number(e.target.value) : undefined)}
          />
        </FormControl>

        <FormControl id="nutrientLevel">
          <FormLabel>Nutrient Level</FormLabel>
          <Input
            type="number"
            value={nutrientLevel === undefined ? "" : nutrientLevel}
            onChange={(e) => setNutrientLevel(e.target.value ? Number(e.target.value) : undefined)}
          />
        </FormControl>

        <FormControl id="measurementTime">
          <FormLabel>Measurement Time (minutes)</FormLabel>
          <Input
            type="number"
            value={measurementTime === undefined ? "" : measurementTime}
            onChange={(e) =>
              setMeasurementTime(e.target.value ? Number(e.target.value) : undefined)
            }
          />
        </FormControl>

        <Button
          mt="2"
          colorScheme="green"
          onClick={handleSubmit}
          isDisabled={
            (identifierType === "device_address" ? !deviceAddress : !labelCode) ||
            moisture === undefined ||
            deviceVersion === undefined
          }
        >
          Submit
        </Button>
      </Container>
    </Navbar>
  );
};

export default AddTrainingDataView;
